import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface ApplicationPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: any;
  onApprove: () => void;
}

export function ApplicationPreviewModal({
  open,
  onOpenChange,
  job,
  onApprove,
}: ApplicationPreviewModalProps) {
  const jobData = job?.job_data || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Preview Application
            {job?.match_score && (
              <Badge className="ml-2">Match: {job.match_score}%</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 flex-1 overflow-hidden">
          {/* Left Side: Job Description */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Job Description</h3>
            <ScrollArea className="h-[calc(80vh-200px)]">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-lg">{jobData.title}</h4>
                  <p className="text-muted-foreground">{jobData.company}</p>
                  <p className="text-sm text-muted-foreground">{jobData.location}</p>
                </div>
                {jobData.salary && (
                  <div>
                    <p className="text-sm font-medium">Salary</p>
                    <p className="text-sm text-muted-foreground">{jobData.salary}</p>
                  </div>
                )}
                {jobData.description && (
                  <div>
                    <p className="text-sm font-medium mb-2">Description</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {jobData.description}
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </Card>

          {/* Right Side: Resume & Cover Letter Preview */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Application Materials</h3>
            <ScrollArea className="h-[calc(80vh-200px)]">
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-2">Resume Preview</h4>
                  <div className="border rounded-lg p-4 bg-muted/30">
                    <p className="text-sm text-muted-foreground">
                      Your AI-optimized resume will be generated when you apply.
                      It will be tailored specifically for this {jobData.title} position at {jobData.company}.
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Cover Letter Preview</h4>
                  <div className="border rounded-lg p-4 bg-muted/30">
                    <p className="text-sm text-muted-foreground">
                      Your personalized cover letter will be generated when you apply.
                      It will highlight your relevant experience and show enthusiasm for the role.
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Application Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Job Type:</span>
                      <span>{jobData.job_type || 'Full-time'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Remote:</span>
                      <span>{jobData.remote || 'On-site'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Match Score:</span>
                      <span className="font-medium">{job?.match_score || 'N/A'}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onApprove}>
            Approve & Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
